import React, { useState } from 'react';
import combatActionsData from '../../data/combat-actions.json';
import { useCharacterStore } from '../../stores/character';
import { getCombatActionMechanic } from '../../services/CombatActionMechanicsService';
import { diceService } from '../../services';
import {
  availableTransitions,
  CARRY_STATE_LABELS,
  describeFailure,
  refusalOf
} from '../../services/CarryStateService';
import { EquipmentModifierService } from '../../services/EquipmentModifierService';
import type { CarryState, Weapon } from '../../types/character';

interface CombatAction {
  id: string;
  name: string;
  length: string;
  subType: string[];
  description: string;
  reference: string;
}

interface CombatCircumstance {
  id: string;
  name: string;
  description: string;
  reference: string;
}

interface CombatActionsReferenceProps {
  /** If provided, only shows this category and hides category tabs */
  fixedCategory?: 'personal' | 'spaceship' | 'aerial';
  /** Optional title override */
  title?: string;
  /** Callback when an action is clicked (for actions with mechanics) */
  onActionClick?: (actionId: string) => void;
}

const CombatActionsReference: React.FC<CombatActionsReferenceProps> = ({
  fixedCategory,
  title = 'Combat Actions Reference',
  onActionClick
}) => {
  // Get character and actions separately to avoid re-render issues
  const character = useCharacterStore((state) => state.character);
  const toggleFavouriteAction = useCharacterStore((state) => state.toggleFavouriteAction);
  const setAimBonus = useCharacterStore((state) => state.setAimBonus);
  const addActiveEffect = useCharacterStore((state) => state.addActiveEffect);
  const removeActiveEffect = useCharacterStore((state) => state.removeActiveEffect);
  const incrementActionCounter = useCharacterStore((state) => state.incrementActionCounter);
  const activeEffects = useCharacterStore((state) => state.activeEffects);
  const addFatigue = useCharacterStore((state) => state.addFatigue);
  const drawWeapon = useCharacterStore((state) => state.drawWeapon);
  const stowWeapon = useCharacterStore((state) => state.stowWeapon);
  const recoverWeapon = useCharacterStore((state) => state.recoverWeapon);
  const [readyRefusal, setReadyRefusal] = useState<string | null>(null);

  /**
   * Perform a Ready transition on a weapon (RT p.242 — draw, stow, or pick up).
   *
   * Deliberately does NOT call `incrementActionCounter`: the store transitions charge
   * their own cost, unlike Aim and Sprint above which have no store action of their
   * own. Ticking here as well would double-charge the Half Action.
   */
  const handleReadyTransition = (weapon: Weapon, to: CarryState) => {
    const result =
      weapon.carryState === 'dropped'
        ? recoverWeapon(weapon.id, to === 'wielded' ? 'wielded' : 'carried')
        : to === 'wielded'
          ? drawWeapon(weapon.id)
          : stowWeapon(weapon.id);
    const refusal = refusalOf(result);
    setReadyRefusal(refusal ? describeFailure(refusal) : null);
  };

  const handleAim = (bonus: 0 | 10 | 20) => {
    setAimBonus(bonus);
    if (bonus === 0) {
      removeActiveEffect('aim');
      return;
    }
    addActiveEffect({
      id: 'aim',
      label: `Aimed (+${bonus})`,
      source: bonus === 10 ? 'Aim (Half Action)' : 'Aim (Full Action)',
      sourceKind: 'aim',
      roundsRemaining: 1,
      createdAt: Date.now()
    });
    incrementActionCounter(bonus === 10 ? 'halfActionsUsed' : 'fullActionsUsed');
  };

  const hasSprint = character?.talents?.some((t) => t.id === 'sprint') ?? false;

  const handleSprintRun = () => {
    // Consecutive Sprint → 1 fatigue (RT core, Sprint talent)
    if (activeEffects['sprint-run']) {
      addFatigue(1);
    }
    addActiveEffect({
      id: 'sprint-run',
      label: 'Sprint Run (x2 move)',
      source: 'Sprint talent (Run action)',
      sourceKind: 'sprint',
      roundsRemaining: 2, // survives into next round for consecutive detection
      createdAt: Date.now()
    });
    incrementActionCounter('fullActionsUsed');
  };

  // Memoize these to prevent creating new arrays on every render
  const aimBonus = React.useMemo(
    () => character?.combatPreferences?.aimBonus || 0,
    [character?.combatPreferences?.aimBonus]
  );

  /**
   * Weapons with a Ready transition available, and what it is.
   *
   * Derived from `availableTransitions` and filtered to Half Action costs, which is
   * exactly what Ready covers: draw, stow, and picking a dropped weapon back up.
   * Dropping is a Free Action and stashing costs nothing, so neither appears here.
   * An `owned` weapon has no Half Action transition, so it is absent — correct, since
   * it is not on the character to be readied.
   */
  const readyableWeapons = React.useMemo(
    () =>
      (character?.weapons ?? [])
        .map((weapon) => ({
          weapon,
          displayName: EquipmentModifierService.applyWeaponModifiers(weapon).displayName,
          transitions: availableTransitions(weapon.carryState, 'weapon').filter(
            (t) => t.cost === 'half'
          )
        }))
        .filter((entry) => entry.transitions.length > 0),
    [character?.weapons]
  );

  const favouritedActions = React.useMemo(
    () => character?.combatPreferences?.favouritedActions || [],
    [character?.combatPreferences?.favouritedActions]
  );

  const [selectedCategory, setSelectedCategory] = useState<'personal' | 'spaceship' | 'aerial'>(
    fixedCategory || 'personal'
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [showCircumstances, setShowCircumstances] = useState(false);
  const [expandedActionId, setExpandedActionId] = useState<string | null>(null);

  // Use fixed category if provided, otherwise use selected
  const activeCategory = fixedCategory || selectedCategory;
  const categoryData = combatActionsData.categories[activeCategory];
  const actions = categoryData.actions as CombatAction[];
  const circumstances = categoryData.circumstances as CombatCircumstance[];

  // Filter actions based on search term
  const filteredActions = actions.filter(
    (action) =>
      action.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      action.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      action.subType.some((type) => type.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Filter circumstances based on search term
  const filteredCircumstances = circumstances.filter(
    (circ) =>
      circ.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      circ.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group actions by length
  // Special handling: "Half/Full" actions appear in both Half and Full sections
  const actionsByLength = filteredActions.reduce(
    (acc, action) => {
      const length = action.length;

      // Handle combined lengths (e.g., "Half/Full")
      if (length.includes('/')) {
        const lengths = length.split('/');
        lengths.forEach((len) => {
          const trimmedLen = len.trim();
          if (!acc[trimmedLen]) {
            acc[trimmedLen] = [];
          }
          acc[trimmedLen].push(action);
        });
      } else {
        if (!acc[length]) {
          acc[length] = [];
        }
        acc[length].push(action);
      }
      return acc;
    },
    {} as Record<string, CombatAction[]>
  );

  const lengthOrder = [
    'Reaction',
    'Half',
    'Full',
    'Move',
    'Extended',
    'Shooting',
    'Evasive',
    'Attack',
    'Varies'
  ];

  // Calculate character bonuses (tens digit of characteristic)
  const agBonus = character ? Math.floor(character.characteristics.agility / 10) : 0;

  // Process description to replace stat references with calculated values
  const processDescription = React.useMemo(() => {
    return (description: string): React.ReactNode => {
      if (!character) {
        // No character loaded, return plain description
        return description;
      }

      const parts: React.ReactNode[] = [];
      let lastIndex = 0;

      // Patterns to match and replace - ORDER MATTERS! More specific patterns first
      const patterns = [
        // Ag Bonus multipliers (must come before general Ag Bonus)
        {
          pattern: 'Ag Bonus x6',
          value: agBonus * 6,
          formula: `Ag Bonus x6 (${agBonus} x 6)`
        },
        {
          pattern: 'Ag Bonus x3',
          value: agBonus * 3,
          formula: `Ag Bonus x3 (${agBonus} x 3)`
        },
        {
          pattern: 'Ag Bonus x2',
          value: agBonus * 2,
          formula: `Ag Bonus x2 (${agBonus} x 2)`
        },
        // Ag Bonus (general) - must come after multipliers
        {
          pattern: 'Ag Bonus',
          value: agBonus,
          formula: `Ag Bonus (${character.characteristics.agility})`
        }
      ];

      // Find all matches with their positions
      interface Match {
        index: number;
        length: number;
        replacement: React.ReactNode;
        text: string;
      }

      const allMatches: Match[] = [];

      patterns.forEach((patternDef, patternIndex) => {
        let searchIndex = 0;
        while (searchIndex < description.length) {
          const index = description
            .toLowerCase()
            .indexOf(patternDef.pattern.toLowerCase(), searchIndex);
          if (index === -1) break;

          const matchText = description.substring(index, index + patternDef.pattern.length);

          allMatches.push({
            index,
            length: patternDef.pattern.length,
            text: matchText,
            replacement: (
              <span
                key={`calc-${patternIndex}-${index}`}
                className="font-semibold text-green-600 dark:text-green-400 cursor-help border-b border-dotted border-green-600 dark:border-green-400"
                title={patternDef.formula || ''}
              >
                {patternDef.value}
              </span>
            )
          });

          searchIndex = index + patternDef.pattern.length;
        }
      });

      // Sort matches by index and remove overlapping matches (keep first match)
      allMatches.sort((a, b) => a.index - b.index);

      const nonOverlappingMatches: Match[] = [];
      let lastEndIndex = 0;

      allMatches.forEach((match) => {
        if (match.index >= lastEndIndex) {
          nonOverlappingMatches.push(match);
          lastEndIndex = match.index + match.length;
        }
      });

      // Build the result with replacements
      nonOverlappingMatches.forEach((match) => {
        if (match.index > lastIndex) {
          parts.push(description.substring(lastIndex, match.index));
        }
        parts.push(match.replacement);
        lastIndex = match.index + match.length;
      });

      // Add remaining text
      if (lastIndex < description.length) {
        parts.push(description.substring(lastIndex));
      }

      return parts.length > 0 ? parts : description;
    };
  }, [character, agBonus]);

  // Get badge color based on action type
  const getActionTypeBadge = (subTypes: string[]) => {
    if (subTypes.includes('Attack'))
      return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200';
    if (subTypes.includes('Movement'))
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200';
    if (subTypes.includes('Concentration'))
      return 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200';
    if (subTypes.includes('Defence'))
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200';
    if (subTypes.includes('Technological'))
      return 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-200';
    if (subTypes.includes('Social'))
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200';
    return 'bg-surface-hover text-ink';
  };

  // Handle action rolls
  const handleDodgeRoll = () => {
    if (!character) return;

    // Find Dodge skill by ID
    const dodgeSkill = character.skills.find((s) => s.id === 'dodge');
    const baseTarget = character.characteristics.agility;
    const skillBonus = dodgeSkill ? diceService.getSkillTrainingBonus(dodgeSkill.training) : -20;
    const target = baseTarget + skillBonus;

    const roll = diceService.rollD100();
    const success = roll <= target;
    const degrees = success ? Math.floor((target - roll) / 10) : Math.floor((roll - target) / 10);

    alert(
      `🛡️ Dodge Roll\nRoll: ${roll} vs ${target}\n${success ? 'Success' : 'Failed'} with ${Math.abs(degrees)} DoS`
    );
  };

  const handleParryRoll = () => {
    if (!character) return;

    const target = character.characteristics.weaponSkill;
    const roll = diceService.rollD100();
    const success = roll <= target;
    const degrees = success ? Math.floor((target - roll) / 10) : Math.floor((roll - target) / 10);

    alert(
      `⚔️ Parry Roll\nRoll: ${roll} vs ${target}\n${success ? 'Success' : 'Failed'} with ${Math.abs(degrees)} DoS`
    );
  };

  // Render individual action with expansion support
  const renderAction = (action: CombatAction, _isInFavourites: boolean) => {
    const hasMechanic = !!getCombatActionMechanic(action.id);
    const isClickable = hasMechanic && onActionClick;
    const isExpanded = expandedActionId === action.id;
    const isFavourited = favouritedActions.includes(action.id);
    const isReaction = action.length === 'Reaction';
    const isDefence = action.subType.includes('Defence');
    const isConcentration = action.subType.includes('Concentration');
    const isAttack = action.subType.includes('Attack');

    return (
      <div
        key={action.id}
        className="p-3 bg-surface border border-line rounded-lg transition-all hover:shadow-md"
      >
        <div
          className="cursor-pointer"
          onClick={() => setExpandedActionId(isExpanded ? null : action.id)}
        >
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h5 className="font-semibold text-ink">{action.name}</h5>
                {isReaction && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavouriteAction(action.id);
                    }}
                    className={`text-lg ${isFavourited ? 'text-yellow-500' : 'text-ink-faint hover:text-yellow-500'}`}
                    title={isFavourited ? 'Remove from favourites' : 'Add to favourites'}
                  >
                    {isFavourited ? '⭐' : '☆'}
                  </button>
                )}
                {isClickable && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-700">
                    ⚔️ Clickable
                  </span>
                )}
                <span className="text-xs text-ink-faint">{isExpanded ? '▼' : '▶'}</span>
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {action.subType.map((type, idx) => (
                  <span
                    key={idx}
                    className={`text-xs px-2 py-0.5 rounded ${getActionTypeBadge(action.subType)}`}
                  >
                    {type}
                  </span>
                ))}
              </div>
            </div>
            <span className="text-xs text-ink-faint ml-2">{action.reference}</span>
          </div>
          <p className="text-sm text-ink whitespace-pre-line">
            {processDescription(action.description)}
          </p>
        </div>

        {/* Expanded Action Controls */}
        {isExpanded && character && (
          <div className="mt-3 pt-3 border-t border-line space-y-2">
            {/* Defence Actions (Dodge/Parry) */}
            {isDefence && (
              <div className="grid grid-cols-2 gap-2">
                {action.name.toLowerCase().includes('dodge') && (
                  <button
                    onClick={handleDodgeRoll}
                    className="px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                  >
                    🛡️ Roll Dodge
                  </button>
                )}
                {action.name.toLowerCase().includes('parry') && (
                  <button
                    onClick={handleParryRoll}
                    className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                  >
                    ⚔️ Roll Parry
                  </button>
                )}
              </div>
            )}

            {/* Attack Actions */}
            {isAttack && isClickable && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onActionClick(action.id);
                }}
                className="w-full px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
              >
                ⚔️ Perform Attack with Equipped Weapons
              </button>
            )}

            {/* Ready — draw, stow or pick up a weapon (RT p.242). The options come
                from CarryStateService, filtered to the Half Action transitions that
                are Ready: dropping is a separate Free Action, and stash/retrieve are
                out-of-combat housekeeping rather than an action. */}
            {action.id === 'ready' && (
              <div className="space-y-2">
                {readyRefusal && (
                  <div
                    role="alert"
                    className="p-2 text-xs bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded text-yellow-900 dark:text-yellow-100"
                  >
                    {readyRefusal}
                  </div>
                )}
                {readyableWeapons.length === 0 ? (
                  <p className="text-xs text-ink-faint">
                    No weapon can be readied. Weapons in the stash must be retrieved from the
                    equipment tab first.
                  </p>
                ) : (
                  readyableWeapons.map(({ weapon, displayName, transitions }) => (
                    <div
                      key={weapon.id}
                      className="flex flex-wrap items-center justify-between gap-2 p-2 bg-surface-alt border border-line rounded"
                    >
                      <span className="text-sm text-ink">
                        {displayName}
                        <span className="ml-2 text-xs text-ink-faint">
                          {CARRY_STATE_LABELS[weapon.carryState].toLowerCase()}
                        </span>
                      </span>
                      <span className="flex gap-2">
                        {transitions.map((transition) => (
                          <button
                            key={transition.to}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReadyTransition(weapon, transition.to);
                            }}
                            className="px-3 py-1 text-sm bg-indigo-600 text-white rounded transition-colors hover:bg-indigo-700"
                          >
                            {transition.label}
                          </button>
                        ))}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Concentration Actions (Aim) */}
            {isConcentration && action.name.toLowerCase().includes('aim') && (
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAim(0);
                  }}
                  className={`px-3 py-2 rounded font-medium transition-colors ${
                    aimBonus === 0
                      ? 'bg-surface-active text-white ring-2 ring-gray-400'
                      : 'bg-surface-active text-white hover:bg-surface-active'
                  }`}
                >
                  Off
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAim(10);
                  }}
                  className={`px-3 py-2 rounded font-medium transition-colors ${
                    aimBonus === 10
                      ? 'bg-green-600 text-white ring-2 ring-green-400'
                      : 'bg-surface-active text-white hover:bg-surface-active'
                  }`}
                >
                  Half (+10)
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAim(20);
                  }}
                  className={`px-3 py-2 rounded font-medium transition-colors ${
                    aimBonus === 20
                      ? 'bg-green-600 text-white ring-2 ring-green-400'
                      : 'bg-surface-active text-white hover:bg-surface-active'
                  }`}
                >
                  Full (+20)
                </button>
              </div>
            )}

            {/* Sprint Run — only for characters with Sprint talent on the Run action */}
            {action.id === 'run' && hasSprint && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSprintRun();
                }}
                className={`w-full px-3 py-2 text-sm rounded font-medium transition-colors ${
                  activeEffects['sprint-run']
                    ? 'bg-amber-600 text-white ring-2 ring-amber-400'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                {activeEffects['sprint-run']
                  ? '⚡ Sprint Again (will cause Fatigue!)'
                  : '⚡ Sprint Run (x2 movement)'}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-ink">{title}</h3>
        {character && (
          <div className="text-xs text-ink-faint">
            Using {character.name}'s stats (Ag Bonus: {agBonus})
          </div>
        )}
      </div>

      {/* Category Tabs - Only show if not fixed */}
      {!fixedCategory && (
        <div className="flex gap-2 border-b border-line">
          <button
            onClick={() => setSelectedCategory('personal')}
            className={`px-4 py-2 font-medium transition-colors ${
              selectedCategory === 'personal'
                ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
                : 'text-ink-dim hover:text-ink-strong'
            }`}
          >
            Personal Combat
          </button>
          <button
            onClick={() => setSelectedCategory('spaceship')}
            className={`px-4 py-2 font-medium transition-colors ${
              selectedCategory === 'spaceship'
                ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
                : 'text-ink-dim hover:text-ink-strong'
            }`}
          >
            Spaceship Combat
          </button>
          <button
            onClick={() => setSelectedCategory('aerial')}
            className={`px-4 py-2 font-medium transition-colors ${
              selectedCategory === 'aerial'
                ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
                : 'text-ink-dim hover:text-ink-strong'
            }`}
          >
            Aerial Combat
          </button>
        </div>
      )}

      {/* Search and Toggle */}
      <div className="flex gap-3">
        <input
          type="text"
          placeholder="Search actions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-3 py-2 border border-line rounded-lg bg-surface text-ink focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <button
          onClick={() => setShowCircumstances(!showCircumstances)}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            showCircumstances
              ? 'bg-blue-600 text-white'
              : 'bg-surface-alt text-ink hover:bg-surface-active'
          }`}
        >
          {showCircumstances ? 'Show Actions' : 'Show Circumstances'}
        </button>
      </div>

      {/* Content */}
      <div className="max-h-[600px] overflow-y-auto">
        {!showCircumstances ? (
          // Actions Display
          <div className="space-y-6">
            {/* Favourited Reactions Section */}
            {favouritedActions.length > 0 && (
              <div>
                <h4 className="text-md font-semibold text-yellow-800 dark:text-yellow-200 mb-3 sticky top-0 bg-page py-2 z-10">
                  ⭐ Favourited Actions ({favouritedActions.length})
                </h4>
                <div className="space-y-3">
                  {filteredActions
                    .filter((action) => favouritedActions.includes(action.id))
                    .map((action) => renderAction(action, true))}
                </div>
              </div>
            )}

            {lengthOrder.map((length) => {
              const actionsForLength = actionsByLength[length];
              if (!actionsForLength || actionsForLength.length === 0) return null;

              return (
                <div key={length}>
                  <h4 className="text-md font-semibold text-ink mb-3 sticky top-0 bg-page py-2 z-10">
                    {length} Action{actionsForLength.length > 1 ? 's' : ''} (
                    {actionsForLength.length})
                  </h4>
                  <div className="space-y-3">
                    {actionsForLength.map((action) => renderAction(action, false))}
                  </div>
                </div>
              );
            })}
            {filteredActions.length === 0 && (
              <div className="text-center py-8 text-ink-faint">
                No actions found matching "{searchTerm}"
              </div>
            )}
          </div>
        ) : (
          // Circumstances Display
          <div className="space-y-3">
            {filteredCircumstances.map((circ) => (
              <div
                key={circ.id}
                className="p-3 bg-surface border border-line rounded-lg hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-2">
                  <h5 className="font-semibold text-ink">{circ.name}</h5>
                  <span className="text-xs text-ink-faint ml-2">{circ.reference}</span>
                </div>
                <p className="text-sm text-ink whitespace-pre-line">
                  {processDescription(circ.description)}
                </p>
              </div>
            ))}
            {filteredCircumstances.length === 0 && (
              <div className="text-center py-8 text-ink-faint">
                No circumstances found matching "{searchTerm}"
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CombatActionsReference;
